import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getMefoDetail } from "../lib/api.js";
import type { MefoProductDetail } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Badge } from "../ui/Badge.js";
import { Breadcrumbs } from "../ui/Breadcrumbs.js";
import {
  PackageIcon,
  DocumentIcon,
  ChevronRightIcon,
  HashIcon,
} from "../lib/icons.js";

/**
 * MEFO detail produktu. Zobrazuje:
 *  - Hero obrázek (přepínatelný mezi primárním a alternativním)
 *  - Název, výrobce, cenu, dodací lhůtu, kód, EAN, skupinu
 *  - HTML popis
 *  - Parametry (Barva, Velikost, …)
 *  - Přílohy (PDF návody, YouTube videa)
 *  - Externí link na mefo.cz (pro objednání)
 */
export function MefoDetailPage(): JSX.Element {
  const { itemId } = useParams<{ itemId: string }>();
  const [data, setData] = useState<MefoProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState<string | null>(null);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMefoDetail(itemId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setActiveImage(d.imgUrl ?? d.imgUrlAlt ?? null);
      })
      .catch((err) => !cancelled && setError(String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  if (loading) return <LoadingBlock label="Načítám MEFO produkt…" />;
  if (error)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error}
        </div>
      </div>
    );
  if (!data)
    return (
      <EmptyState
        title="Produkt nenalezen"
        description="Zkontrolujte MEFO ID v URL."
      />
    );

  const images = [data.imgUrl, data.imgUrlAlt].filter((u): u is string => Boolean(u));
  const inStock = data.deliveryDate === 0;
  const shortDelivery =
    data.deliveryDate != null && data.deliveryDate > 0 && data.deliveryDate <= 3;
  const deliveryLabel =
    data.deliveryDate == null
      ? null
      : data.deliveryDate === 0
        ? "Skladem"
        : `${data.deliveryDate} ${data.deliveryDate === 1 ? "den" : data.deliveryDate < 5 ? "dny" : "dnů"}`;

  const documents = data.attachments.filter((a) => a.kind === "document");
  const videos = data.attachments.filter((a) => a.kind === "video");
  const otherAtt = data.attachments.filter((a) => a.kind === "other");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <Breadcrumbs
          items={[
            { label: "MEFO", to: "/mefo" },
            ...(data.manufacturer
              ? [{ label: data.manufacturer, to: `/mefo?manufacturer=${encodeURIComponent(data.manufacturer)}` }]
              : []),
            { label: data.name },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,480px)_1fr] gap-8">
        {/* Hero / gallery */}
        <div>
          <div className="aspect-square bg-white rounded-2xl border border-zinc-200 shadow-card flex items-center justify-center overflow-hidden">
            {activeImage ? (
              <img
                src={activeImage}
                alt={data.name}
                className="w-full h-full object-contain p-6"
              />
            ) : (
              <div className="text-zinc-300">
                <PackageIcon size={48} />
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              {images.map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setActiveImage(src)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                    activeImage === src ? "border-ktm-orange" : "border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  <img src={src} alt="" className="w-full h-full object-contain p-2 bg-zinc-50" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Main info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Badge tone="orange">MEFO</Badge>
            {data.manufacturer && (
              <span className="text-sm font-semibold text-zinc-600">
                {data.manufacturer}
              </span>
            )}
            {data.skupina && (
              <>
                <span className="text-zinc-300">·</span>
                <span className="text-sm text-zinc-500">{data.skupina}</span>
              </>
            )}
          </div>

          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
            {data.name}
          </h1>

          {data.productName && data.productName !== data.name && (
            <p className="mt-1 text-sm text-zinc-500">{data.productName}</p>
          )}

          {/* Price + delivery */}
          <div className="mt-5 flex items-end justify-between flex-wrap gap-3">
            <div>
              {data.priceGross != null ? (
                <>
                  <div className="text-3xl font-semibold text-zinc-900">
                    {data.priceGross.toLocaleString("cs-CZ", {
                      maximumFractionDigits: 0,
                    })}{" "}
                    Kč
                  </div>
                  {data.priceNet != null && (
                    <div className="text-sm text-zinc-500">
                      {data.priceNet.toLocaleString("cs-CZ", {
                        maximumFractionDigits: 0,
                      })}{" "}
                      Kč bez DPH ({data.vatRate}%)
                    </div>
                  )}
                </>
              ) : (
                <span className="text-zinc-400">Bez ceny</span>
              )}
            </div>
            {deliveryLabel && (
              <Badge tone={inStock ? "green" : shortDelivery ? "amber" : "neutral"}>
                {deliveryLabel}
              </Badge>
            )}
          </div>

          {/* Meta */}
          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <MetaItem label="Kód zboží">
              <span className="font-mono">{data.kodZbozi}</span>
            </MetaItem>
            <MetaItem label="MEFO ID">
              <span className="font-mono text-xs">{data.itemId}</span>
            </MetaItem>
            {data.ean && (
              <MetaItem label="EAN">
                <span className="font-mono">{data.ean}</span>
              </MetaItem>
            )}
            {data.skupinaKod && (
              <MetaItem label="Kód skupiny">
                <span className="font-mono">{data.skupinaKod}</span>
              </MetaItem>
            )}
            {data.categoryText && (
              <div className="col-span-full">
                <dt className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
                  Kategorie
                </dt>
                <dd className="mt-1 text-sm text-zinc-700">{data.categoryText}</dd>
              </div>
            )}
          </dl>

          {/* External link CTA */}
          {data.externalUrl && (
            <a
              href={data.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-ktm-orange hover:bg-ktm-orange-600 text-white text-sm font-medium transition-colors"
            >
              Otevřít na mefo.cz
              <ChevronRightIcon size={14} />
            </a>
          )}
        </div>
      </div>

      {/* Params */}
      {data.params.length > 0 && (
        <section className="mt-10">
          <h2 className="text-base font-semibold uppercase tracking-tight text-zinc-900 mb-3">
            Parametry
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.params.map((p, i) => (
              <div
                key={`${p.name}-${i}`}
                className="flex items-center justify-between bg-white rounded-lg border border-zinc-200 px-4 py-2"
              >
                <dt className="text-sm text-zinc-500">{p.name}</dt>
                <dd className="text-sm font-medium text-zinc-900">{p.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* Description */}
      {data.descriptionHtml && (
        <section className="mt-10">
          <h2 className="text-base font-semibold uppercase tracking-tight text-zinc-900 mb-3">
            Popis
          </h2>
          <div
            className="prose prose-sm max-w-none bg-white rounded-2xl border border-zinc-200 p-6 shadow-card"
            dangerouslySetInnerHTML={{ __html: data.descriptionHtml }}
          />
        </section>
      )}

      {/* Attachments: documents */}
      {documents.length > 0 && (
        <section className="mt-10">
          <h2 className="text-base font-semibold uppercase tracking-tight text-zinc-900 mb-3">
            Dokumenty a návody{" "}
            <span className="font-normal text-zinc-400">{documents.length}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {documents.map((a) => (
              <a
                key={a.attachmentId}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 bg-white hover:border-ktm-orange hover:shadow-sm transition-all group"
              >
                <div className="w-10 h-10 rounded bg-zinc-100 flex items-center justify-center text-zinc-500 group-hover:text-ktm-orange group-hover:bg-orange-50 transition-colors shrink-0">
                  <DocumentIcon size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900 truncate">
                    {a.name ?? `Příloha ${a.attachmentId}`}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {new URL(a.url).hostname}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Attachments: videos */}
      {videos.length > 0 && (
        <section className="mt-10">
          <h2 className="text-base font-semibold uppercase tracking-tight text-zinc-900 mb-3">
            Videa{" "}
            <span className="font-normal text-zinc-400">{videos.length}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {videos.map((a) => (
              <a
                key={a.attachmentId}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 bg-white hover:border-ktm-orange hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 rounded bg-red-50 flex items-center justify-center text-red-600 shrink-0">
                  ▶
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900 truncate">
                    {a.name ?? "Video"}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{a.url}</div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Other attachments (rare) */}
      {otherAtt.length > 0 && (
        <section className="mt-10">
          <h2 className="text-base font-semibold uppercase tracking-tight text-zinc-900 mb-3">
            Další přílohy
          </h2>
          <div className="space-y-1 text-sm">
            {otherAtt.map((a) => (
              <a
                key={a.attachmentId}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-zinc-700 hover:text-ktm-orange"
              >
                <HashIcon size={12} />
                <span>{a.name ?? a.url}</span>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function MetaItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-zinc-700">{children}</dd>
    </div>
  );
}
